import React, { useState } from 'react';
import { ArrowRight } from 'lucide-react';
import { useT, useLang, itemHasLocale } from '../lib/i18n';
import { BACKEND_URL as API } from '../lib/config';

// ─────────────────────────────────────────────────────────────────────────
// ModernLogos — «Moderno» theme Partners + Clients (s139, approved by Anthony):
//   ModernPartners = L2 "Hairline Grid": one name/logo per cell, thin lines,
//                    the pointed cell lightens; links when the item has one.
//   ModernClients  = L1 "Wordmark Band": a band between two thin lines with the
//                    names/logos scrolling slowly (config autoscroll +
//                    scroll_speed, pauses on hover, still with reduced motion).
// Item = name/logo_url/link_url. A logo shows greyscale → colour on hover; no
// logo → the name in serif. Header from each section's CMS config.
// ─────────────────────────────────────────────────────────────────────────

const SERIF = "'Playfair Display', Georgia, serif";
const img = (src) => (src && src.startsWith('/api') ? `${API}${src}` : src);

const CSS = `
.mlg-logo{filter:grayscale(1);opacity:.75;transition:filter 250ms ease,opacity 250ms ease}
.mlg-cell:hover .mlg-logo,.mlg-chip:hover .mlg-logo{filter:none;opacity:1}
.mlg-track{animation:mlgMove var(--mlg-dur,30s) linear infinite}
.mlg-band:hover .mlg-track{animation-play-state:paused}
@keyframes mlgMove{from{transform:translateX(0)}to{transform:translateX(-50%)}}
@media (prefers-reduced-motion:reduce){.mlg-track{animation:none}}
`;

function Header({ config, fallback }) {
  const tt = useT();
  const title = tt(config.title) || fallback;
  const words = title.trim().split(/\s+/);
  const last = words.length > 1 ? words.pop() : null;
  const ctaText = tt(config.cta_text);
  const ctaUrl = tt(config.cta_url);
  const cta = ctaText && ctaUrl && ctaUrl !== '/#' && ctaUrl !== '#';
  return (
    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
      <div className="flex flex-col gap-4 max-w-2xl">
        {tt(config.eyebrow) && (
          <div className="inline-flex items-center gap-3 text-[11px] font-bold uppercase" style={{ letterSpacing: '.2em', color: 'var(--accent-strong, #b86500)' }}>
            <span style={{ width: 32, height: 2, background: 'var(--color-accent, #ff9c1b)' }} />{tt(config.eyebrow)}
          </div>
        )}
        <h2 className="font-bold" style={{ fontFamily: SERIF, fontSize: 'clamp(2rem, 3.4vw, 3rem)', lineHeight: 1.08, color: 'var(--color-heading, #1a2332)', textWrap: 'balance' }}>
          {words.join(' ')}{last && <> <em style={{ fontStyle: 'italic', fontWeight: 500, color: 'var(--accent-strong, #b86500)' }}>{last}</em></>}
        </h2>
        {tt(config.subtitle) && <p className="text-base md:text-lg leading-relaxed" style={{ color: 'var(--color-body-text, #475569)' }}>{tt(config.subtitle)}</p>}
      </div>
      {cta && (
        <a href={ctaUrl} target={config.cta_new_tab ? '_blank' : '_self'} rel="noopener noreferrer" className="mx-link inline-flex items-center gap-2 font-bold text-sm self-start md:self-end" style={{ color: 'var(--accent-strong, #b86500)' }}>
          {ctaText} <ArrowRight className="w-4 h-4" />
        </a>
      )}
    </div>
  );
}

function Mark({ item, size = 'md' }) {
  const tt = useT();
  const [broken, setBroken] = useState(false);
  const src = img(item.logo_url);
  // Partner cells give logos more room (wide wordmarks and stacked marks stay legible).
  const box = size === 'cell' ? 'max-h-12 max-w-[190px]' : 'max-h-10 max-w-[150px]';
  if (src && !broken) return <img src={src} alt={tt(item.name)} onError={() => setBroken(true)} className={`mlg-logo ${box} object-contain`} />;
  return <span className="font-semibold whitespace-nowrap" style={{ fontFamily: SERIF, fontSize: size === 'lg' ? 'clamp(1.25rem, 1.9vw, 1.7rem)' : '1.1rem', color: 'var(--color-heading, #1a2332)' }}>{tt(item.name)}</span>;
}

const useItems = (items) => {
  const { lang } = useLang();
  return (items || []).filter(i => i.visible !== false && itemHasLocale(i.name, lang));
};

export function ModernPartners({ config = {}, items = [] }) {
  const list = useItems(items);
  if (!list.length) return null;
  const line = 'rgba(20,26,58,.14)';
  return (
    <section id="aurex-partners" data-testid="aurex-section-partners" style={{ background: 'color-mix(in srgb, var(--color-accent, #ff9c1b) 5%, #f6f5f2)' }}>
      <style>{CSS}</style>
      <div className="max-w-[1440px] mx-auto px-5 sm:px-6 lg:px-[clamp(24px,5vw,80px)] py-20 lg:py-28">
        <Header config={config} fallback="Our partners" />
        <div className="grid grid-cols-2 md:grid-cols-4" style={{ borderTop: `1px solid ${line}`, borderLeft: `1px solid ${line}` }}>
          {list.map(p => {
            const inner = <Mark item={p} size="cell" />;
            const cls = 'mlg-cell flex items-center justify-center text-center px-4 py-8 md:py-10 min-h-[96px] transition-colors duration-200 hover:bg-white';
            const style = { borderRight: `1px solid ${line}`, borderBottom: `1px solid ${line}` };
            return p.link_url
              ? <a key={p.id} href={p.link_url} target="_blank" rel="noopener noreferrer" className={cls} style={style} data-testid={`partner-${p.id}`}>{inner}</a>
              : <div key={p.id} className={cls} style={style} data-testid={`partner-${p.id}`}>{inner}</div>;
          })}
        </div>
      </div>
    </section>
  );
}

export function ModernClients({ config = {}, items = [] }) {
  const list = useItems(items);
  if (!list.length) return null;
  const moving = config.autoscroll !== false && list.length > 1;
  const speed = Math.max(8, Number(config.scroll_speed) || 30);
  // Repeat so one half of the track is always wider than the viewport.
  const reps = Math.max(2, Math.ceil(12 / list.length));
  const half = Array.from({ length: reps }, () => list).flat();
  const Chip = ({ item, k }) => (
    <span key={k} className="mlg-chip flex items-center gap-10 md:gap-14 pr-10 md:pr-14">
      {item.link_url ? <a href={item.link_url} target="_blank" rel="noopener noreferrer"><Mark item={item} size="lg" /></a> : <Mark item={item} size="lg" />}
      <span aria-hidden="true" style={{ color: 'var(--color-accent, #ff9c1b)', fontSize: '.7rem' }}>◆</span>
    </span>
  );
  return (
    <section id="aurex-clients" data-testid="aurex-section-clients" className="bg-white">
      <style>{CSS}</style>
      <div className="max-w-[1440px] mx-auto px-5 sm:px-6 lg:px-[clamp(24px,5vw,80px)] pt-20 lg:pt-28 pb-10">
        <Header config={config} fallback="Our clients" />
      </div>
      <div className="mlg-band overflow-hidden border-y mb-20 lg:mb-28" style={{ borderColor: 'rgba(20,26,58,.12)' }} aria-label={list.map(i => i.name && (i.name.en || i.name)).join(', ')}>
        {moving ? (
          <div className="mlg-track flex w-max py-7 md:py-8" style={{ '--mlg-dur': `${speed * (half.length / 6)}s` }} aria-hidden="true">
            {[0, 1].map(k => <div key={k} className="flex shrink-0 pl-10 md:pl-14">{half.map((it, i) => <Chip key={`${k}-${i}`} item={it} k={`${k}-${i}`} />)}</div>)}
          </div>
        ) : (
          <div className="flex flex-wrap justify-center py-7 md:py-8 px-5">{list.map((it, i) => <Chip key={i} item={it} k={i} />)}</div>
        )}
      </div>
    </section>
  );
}
